import React, { useState } from 'react';
import { collection, doc, getDocs, query, where, writeBatch } from 'firebase/firestore';
import type { Period, Student, AttRec, Session } from '../types';
import { TOK, D1, FONT, d1, freqColor, SitBadge, NeutralBadge } from '../design/tokens';
import { escapeHtml } from '../utils/crypto';
import { db, periodBase } from '../firebase/config';

export interface RelatoriosProps {
  periods: Period[]; viewPeriodId: string; setViewPeriodId: (id: string) => void; isViewingActive: boolean;
  students: Student[]; attendance: AttRec[]; totalAulas: number;
  sessions: Session[];
  onDeleteAttendance: (studentId: string, sessionId: string) => void;
  onAddAttendance: (student: { id: string; name: string; dre: string; turma: string }, sessionId: string) => void;
  darkMode: boolean;
}

export function RelatoriosTab({ periods, viewPeriodId, setViewPeriodId, isViewingActive, students, attendance, totalAulas, sessions, onDeleteAttendance, onAddAttendance, darkMode }: RelatoriosProps) {
  const T = TOK;
  const [rTurma,setRTurma]=useState<"A"|"B">("A");
  const [busca,setBusca]=useState("");
  const [apenasRisco,setApenasRisco]=useState(false);
  const [sortBy,setSortBy]=useState<"name"|"pct">("name");
  const [confirmDel,setConfirmDel]=useState<{studentId:string;studentName:string;sessionId:string}|null>(null);
  const [confirmDelSession,setConfirmDelSession]=useState<{sessionId:string}|null>(null);
  const [studentDetail,setStudentDetail]=useState<{id:string;name:string;dre:string}|null>(null);

  // Bulk edit em massa
  const [bulkEditSession,setBulkEditSession]=useState<{sessionId:string;label:string}|null>(null);
  const [bulkState,setBulkState]=useState<Record<string,boolean>>({});
  const [bulkFilter,setBulkFilter]=useState<"all"|"present"|"absent">("all");
  const [bulkSearch,setBulkSearch]=useState("");
  const [bulkSaving,setBulkSaving]=useState(false);

  const periodLabel = periods.find(p=>p.id===viewPeriodId)?.label || viewPeriodId;

  const sessionsForTurma=(()=>{
    return sessions.filter(s=>s.turma===rTurma).sort((a,b)=>{
      const ta=a.createdAt?.toDate?a.createdAt.toDate().getTime():new Date(a.createdAt).getTime();
      const tb=b.createdAt?.toDate?b.createdAt.toDate().getTime():new Date(b.createdAt).getTime();
      return ta-tb;
    }).map(s=>s.sessionId||s.id);
  })();

  // Mais recentes à esquerda no visual. Exports continuam cronológicos.
  const sessionsDisplay = [...sessionsForTurma].reverse();

  const sessLabel=(sId:string)=>{
    const sess=sessions.find(s=>s.sessionId===sId||s.id===sId);
    if(sess?.tema) return sess.tema;
    const parts=sId.split("-"); const tsStr=parts[parts.length-1]; const ts=parseInt(tsStr,10);
    if(isNaN(ts)||ts<1000000000000)return sId;
    return new Date(ts).toLocaleDateString("pt-BR",{weekday:"short",day:"2-digit",month:"2-digit"});
  };

  const sessDate=(sId:string)=>{
    const sess=sessions.find(s=>s.sessionId===sId||s.id===sId);
    if(sess?.createdAt){
      const d=sess.createdAt?.toDate?sess.createdAt.toDate():new Date(sess.createdAt);
      return d.toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"});
    }
    const parts=sId.split("-"); const tsStr=parts[parts.length-1]; const ts=parseInt(tsStr,10);
    if(isNaN(ts)||ts<1000000000000)return "";
    return new Date(ts).toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"});
  };


  // Cada aluno aparece apenas na turma em que esta matriculado hoje. Quem foi
  // transferido leva as presencas da turma anterior consigo (ver presenteEm).
  const turmaStudents=students.filter(s=>s.turma===rTurma).sort((a,b)=>a.name.localeCompare(b.name));

  const hasAtt=(studentId:string,sessionId:string)=>attendance.some(a=>a.studentId===studentId&&a.sessionId===sessionId);

  // Dia (AAAA-MM-DD) de cada sessao, das DUAS turmas. As turmas tem aula nos
  // mesmos dias, entao o dia identifica a aula independentemente da turma.
  const sessDayMap=(()=>{
    const m:Record<string,string>={};
    sessions.forEach(s=>{
      const d=s.createdAt?.toDate?s.createdAt.toDate():new Date(s.createdAt);
      if(!isNaN(d.getTime())) m[s.sessionId||s.id]=d.toISOString().slice(0,10);
    });
    return m;
  })();

  // sessionId real onde a presenca do aluno esta gravada para aquela aula —
  // pode ser a sessao da turma antiga, se o aluno foi transferido. null = ausente.
  const attSidFor=(studentId:string,sessionId:string):string|null=>{
    if(hasAtt(studentId,sessionId))return sessionId;
    const dia=sessDayMap[sessionId];
    if(!dia)return null;
    const rec=attendance.find(a=>a.studentId===studentId&&sessDayMap[a.sessionId]===dia);
    return rec?rec.sessionId:null;
  };
  const presenteEm=(studentId:string,sessionId:string)=>attSidFor(studentId,sessionId)!==null;

  const getStudentPct=(studentId:string)=>{
    if(sessionsForTurma.length===0)return 0;
    const pres=sessionsForTurma.filter(sid=>presenteEm(studentId,sid)).length;
    return Math.round((pres/sessionsForTurma.length)*100);
  };

  const getSessionRate=(sessionId:string)=>{
    if(turmaStudents.length===0)return 0;
    const pres=turmaStudents.filter(s=>presenteEm(s.id,sessionId)).length;
    return Math.min(100,Math.round((pres/turmaStudents.length)*100));
  };

  // Aluno com inscricao trancada nao conta como "em risco" nem entra na media:
  // a ausencia dele e administrativa, nao reprovacao por frequencia.
  const turmaAtivos=turmaStudents.filter(s=>!s.blocked);
  const riskCount=turmaAtivos.filter(s=>getStudentPct(s.id)<75).length;
  const avgFreq=turmaAtivos.length>0?Math.round(turmaAtivos.reduce((acc,s)=>acc+getStudentPct(s.id),0)/turmaAtivos.length):0;
  const activeSessions=sessions.filter(s=>s.active);

  const filteredStudents=turmaStudents.filter(s=>{
    if(apenasRisco&&(s.blocked||getStudentPct(s.id)>=75))return false;
    if(busca){const b=busca.toLowerCase();return s.name.toLowerCase().includes(b)||s.dre.toLowerCase().includes(b);}
    return true;
  }).sort((a,b)=>{
    if(sortBy==="pct"){
      const diff=getStudentPct(a.id)-getStudentPct(b.id);
      if(diff!==0)return diff;
    }
    return a.name.localeCompare(b.name);
  });

  const exportCSV=()=>{
    const header=["Período","DRE","Nome",...sessionsForTurma.map(sessLabel),"Total","Freq%","Status"].join(";");
    const rows=filteredStudents.map(s=>{
      const cols=sessionsForTurma.map(sid=>presenteEm(s.id,sid)?"P":"F");
      const total=cols.filter(c=>c==="P").length;
      const pct=getStudentPct(s.id);
      const status=s.blocked?"INSCRICAO TRANCADA":(pct>=75?"APROVADO":"REPROVADO");
      return [periodLabel,s.dre,s.name,...cols,total,pct+"%",status].join(";");
    });
    const csv="\uFEFF"+[header,...rows].join("\n");
    const blob=new Blob([csv],{type:"text/csv;charset=utf-8;"}); const a=document.createElement("a"); a.href=URL.createObjectURL(blob); a.download=`frequencia_${periodLabel}_turma${rTurma}.csv`; a.click();
  };

  const exportPDF=()=>{
    const hdr=`<tr style="background:#0a1128;color:white"><th>DRE</th><th>Nome</th>${sessionsForTurma.map(s=>`<th>${escapeHtml(sessLabel(s))}</th>`).join("")}<th>Total</th><th>%</th><th>Status</th></tr>`;
    const rows=filteredStudents.map(s=>{
      const cols=sessionsForTurma.map(sid=>presenteEm(s.id,sid)?'<td style="color:green;text-align:center">P</td>':'<td style="color:red;text-align:center">F</td>');
      const total=sessionsForTurma.filter(sid=>presenteEm(s.id,sid)).length;
      const pct=getStudentPct(s.id);
      const bloq=!!s.blocked;
      const cor=bloq?"#64748b":(pct>=75?"green":"red");
      const status=bloq?"INSCRIÇÃO TRANCADA":(pct>=75?"APROVADO":"REPROVADO");
      return `<tr${bloq?' style="background:#f1f5f9;color:#64748b"':''}><td>${escapeHtml(s.dre)}</td><td>${escapeHtml(s.name)}</td>${cols.join("")}<td style="text-align:center;font-weight:bold">${total}</td><td style="text-align:center;font-weight:bold;color:${cor}">${pct}%</td><td style="text-align:center;font-weight:bold;color:${cor}">${status}</td></tr>`;
    }).join("");
    const html=`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Frequência ${periodLabel} Turma ${rTurma}</title><style>body{font-family:"IBM Plex Sans",Arial,sans-serif;font-size:11px;padding:20px;color:#1c2430}table{border-collapse:collapse;width:100%}th{font-size:9.5px;letter-spacing:.6px;text-transform:uppercase;color:#8a93a0;border-bottom:1.5px solid #1c2430;padding:6px;text-align:center}td{border-bottom:1px solid #ece9e2;padding:5px 6px}.cap{font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:#8a93a0}h2{font-family:"Source Serif 4",Georgia,serif;font-weight:600;font-size:22px;margin:4px 0 14px}</style></head><body><div class="cap">GEO Frequência · Diário de classe · ${periodLabel}</div><h2>Frequência — Turma ${rTurma}</h2><table>${hdr}${rows}</table></body></html>`;
    const w=window.open("","_blank","width=900,height=700");
    if(w){w.document.write(html);w.document.close();setTimeout(()=>w.print(),500);}else{alert('Não foi possível abrir a janela de impressão. Permita pop-ups neste site.');}
  };

  // Abre modal de edição em massa para uma aula
  const openBulkEdit=(sid:string)=>{
    const initial:Record<string,boolean>={};
    for(const s of turmaStudents){ initial[s.id]=hasAtt(s.id,sid); }
    setBulkState(initial);
    setBulkEditSession({sessionId:sid,label:sessLabel(sid)});
    setBulkFilter("all");
    setBulkSearch("");
  };

  // Calcula diff do estado atual vs. inicial
  const bulkChangeCount=(()=>{
    if(!bulkEditSession) return 0;
    let n=0;
    for(const s of turmaStudents){
      if(s.blocked) continue; // trancado nao entra na marcacao
      const cur=hasAtt(s.id,bulkEditSession.sessionId);
      const nxt=bulkState[s.id]??cur;
      if(cur!==nxt)n++;
    }
    return n;
  })();

  const bulkPresentCount=(()=>{
    if(!bulkEditSession) return 0;
    return turmaStudents.filter(s=>bulkState[s.id]).length;
  })();

  // Lista filtrada para exibir no modal
  const bulkVisibleStudents=(()=>{
    if(!bulkEditSession) return [];
    return turmaStudents.filter(s=>{
      const isPresent=bulkState[s.id];
      if(bulkFilter==="present"&&!isPresent) return false;
      if(bulkFilter==="absent"&&isPresent) return false;
      if(bulkSearch){
        const b=bulkSearch.toLowerCase();
        if(!s.name.toLowerCase().includes(b)&&!s.dre.toLowerCase().includes(b)) return false;
      }
      return true;
    });
  })();

  // Os atalhos em massa ignoram quem esta com inscricao trancada.
  const bulkMarkAllVisible=(value:boolean)=>{
    setBulkState(prev=>{
      const next={...prev};
      for(const s of bulkVisibleStudents){ if(s.blocked) continue; next[s.id]=value; }
      return next;
    });
  };

  const bulkInvertVisible=()=>{
    setBulkState(prev=>{
      const next={...prev};
      for(const s of bulkVisibleStudents){ if(s.blocked) continue; next[s.id]=!next[s.id]; }
      return next;
    });
  };

  const bulkSave=async()=>{
    if(!bulkEditSession) return;
    const sid=bulkEditSession.sessionId;
    setBulkSaving(true);
    try{
      const batch=writeBatch(db);
      const attCol=collection(db,periodBase(viewPeriodId)+'/attendance');
      for(const s of turmaStudents){
        if(s.blocked) continue; // trancado nunca recebe presenca por marcacao em massa
        const cur=hasAtt(s.id,sid);
        const nxt=bulkState[s.id]??cur;
        if(cur===nxt) continue;
        if(nxt&&!cur){
          const attDocId=(s.id+'_'+sid).replace(/\//g,'_');
          batch.set(doc(attCol,attDocId),{studentId:s.id,studentName:s.name,dre:s.dre,turma:s.turma,sessionId:sid,timestamp:new Date(),periodId:viewPeriodId});
        }else if(!nxt&&cur){
          const existing=attendance.find(a=>a.studentId===s.id&&a.sessionId===sid);
          if(existing?.id) batch.delete(doc(attCol,existing.id));
        }
      }
      await batch.commit();
      setBulkEditSession(null);
    }catch(e:unknown){
      alert("Erro ao salvar: "+(e instanceof Error?e.message:""));
    }finally{
      setBulkSaving(false);
    }
  };

  const bulkCancel=()=>{
    if(bulkChangeCount>0){
      if(!confirm(`Descartar ${bulkChangeCount} alteração(ões) não salva(s)?`)) return;
    }
    setBulkEditSession(null);
  };

  return (
    <div style={{background:T.bg,color:T.ink,fontFamily:T.sans,fontSize:14}}>
      <div style={{padding:"4px 0 60px"}}>

        {/* Cabeçalho */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",gap:16,marginBottom:16,flexWrap:"wrap"}}>
          <div>
            <div style={d1.cap}>Diário de classe · {periodLabel}</div>
            <h1 style={{...d1.h1,marginTop:6}}>Frequência — Turma {rTurma}</h1>
          </div>
          <div className="gf-noprint" style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <button style={d1.btnO} onClick={()=>setRTurma(rTurma==="A"?"B":"A")}>Turma {rTurma==="A"?"B":"A"}</button>
            <button style={d1.btnO} onClick={exportCSV}>Exportar CSV</button>
            <button style={d1.btn} onClick={exportPDF}>Imprimir PDF</button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="gf-noprint" style={{display:"flex",gap:10,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
          <input style={{...d1.inp,width:260}} placeholder="Buscar por nome ou DRE" value={busca} onChange={e=>setBusca(e.target.value)} />
          <select style={{...d1.inp,width:"auto",cursor:"pointer"}} value={sortBy} onChange={e=>setSortBy(e.target.value as "name"|"pct")} title="Ordenar alunos">
            <option value="name">Ordenar por nome</option>
            <option value="pct">Ordenar por frequência (risco primeiro)</option>
          </select>
          <button onClick={()=>setApenasRisco(!apenasRisco)} style={{...(apenasRisco?d1.btnR:d1.btnO),...(apenasRisco?{}:{color:D1.alert,borderColor:D1.line})}}>Só em risco ({riskCount})</button>
          <span style={{marginLeft:"auto",fontSize:12.5,color:D1.ink2}}>{sessionsForTurma.length} aula{sessionsForTurma.length!==1?"s":""} de {totalAulas||"—"} · média <b style={{fontFamily:FONT.mono,color:freqColor(avgFreq)}}>{avgFreq}%</b>{activeSessions.find(s=>s.turma===rTurma)?<span style={{color:D1.ok,fontWeight:600}}> · ● chamada aberta</span>:null}</span>
        </div>

        {/* Legenda */}
        <div style={{display:"flex",gap:18,fontSize:12.5,color:D1.ink2,marginBottom:10,flexWrap:"wrap"}}>
          <span><b style={{color:D1.ok,fontFamily:FONT.mono}}>P</b> presente</span>
          <span><b style={{color:D1.alert,fontFamily:FONT.mono}}>F</b> falta</span>
          <span><b style={{color:D1.ok,fontFamily:FONT.mono}}>P*</b> presença na turma anterior</span>
          <span><b style={{color:D1.ink3,fontFamily:FONT.mono}}>–</b> inscrição trancada</span>
          <span className="gf-noprint" style={{marginLeft:"auto"}}>Clique em uma célula para corrigir · no nome da aula para editar em massa</span>
        </div>

        {/* Attendance matrix table */}
        {sessionsForTurma.length===0?(
          <div style={{textAlign:"center",color:D1.ink2,fontSize:14,padding:"48px 0",background:"#fff",border:`1px solid ${D1.line}`}}>
            Nenhuma aula para a Turma {rTurma} em {periodLabel}.
          </div>
        ):(
          <div style={{background:"#fff",border:`1px solid ${D1.line}`}}>
            <div className="gf-rel-scroll" style={{overflow:"auto",maxHeight:600}}>
              <table style={{width:"100%",borderCollapse:"separate",borderSpacing:0,fontSize:13}}>
                <thead>
                  <tr>
                    <th style={{...d1.th,position:"sticky",left:0,top:0,zIndex:3,minWidth:100,whiteSpace:"nowrap"}}>DRE</th>
                    <th style={{...d1.th,position:"sticky",left:100,top:0,zIndex:3,minWidth:220}}>Nome</th>
                    {sessionsDisplay.map(sid=>{
                      const rate=getSessionRate(sid);
                      return (
                        <th key={sid} style={{...d1.th,position:"sticky",top:0,zIndex:1,textAlign:"center",padding:"8px 4px",minWidth:62,borderLeft:`1px solid ${D1.line2}`,whiteSpace:"nowrap"}}>
                          <div style={{fontFamily:FONT.mono,color:D1.ink,fontSize:12,cursor:"pointer",textTransform:"none",letterSpacing:0}} onClick={()=>openBulkEdit(sid)} title={sessLabel(sid)+" — clique para editar em massa"}>{sessDate(sid)||"—"}</div>
                          <div style={{fontSize:9.5,letterSpacing:0.3,maxWidth:72,overflow:"hidden",textOverflow:"ellipsis",margin:"0 auto",cursor:"pointer"}} onClick={()=>openBulkEdit(sid)} title={sessLabel(sid)}>{sessLabel(sid)}</div>
                          <div style={{fontFamily:FONT.mono,fontSize:9.5,color:freqColor(rate),marginTop:2,textTransform:"none"}}>{rate}%</div>
                          <div className="gf-noprint" style={{display:"flex",gap:8,justifyContent:"center",marginTop:2}}>
                            <button onClick={()=>openBulkEdit(sid)} title="Editar em massa" style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:D1.acc,padding:0,lineHeight:1,fontWeight:700}}>✎</button>
                            <button onClick={()=>setConfirmDelSession({sessionId:sid})} title="Excluir aula" style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:D1.ink3,padding:0,lineHeight:1}}>✕</button>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{...d1.th,position:"sticky",right:118,top:0,zIndex:3,textAlign:"right",whiteSpace:"nowrap",minWidth:70}}>Freq.</th>
                    <th style={{...d1.th,position:"sticky",right:0,top:0,zIndex:3,whiteSpace:"nowrap",minWidth:118}}>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map(s=>{
                    const pct=getStudentPct(s.id);
                    const bloq=!!s.blocked;
                    const risk=!bloq&&pct<75;
                    const pres=sessionsForTurma.filter(sid=>presenteEm(s.id,sid)).length;
                    const bgFixa=bloq?"#f6f5f1":(risk?D1.riskRow:"#fff");
                    return (
                      <tr key={s.id} style={{background:bgFixa,color:bloq?D1.ink3:D1.ink}}>
                        <td style={{...d1.td,position:"sticky",left:0,background:bgFixa,zIndex:1,fontFamily:FONT.mono,fontSize:12,color:D1.ink2}}>{s.dre}</td>
                        <td style={{...d1.td,position:"sticky",left:100,background:bgFixa,zIndex:1,fontSize:13,whiteSpace:"nowrap"}}>
                          <span style={{cursor:"pointer",color:bloq?D1.ink3:D1.ink}} onClick={()=>setStudentDetail({id:s.id,name:s.name,dre:s.dre})} title="Ver histórico do aluno">{s.name}</span>
                        </td>
                        {sessionsDisplay.map(sid=>{
                          // realSid = onde a presenca esta gravada (pode ser a sessao
                          // da turma anterior, se o aluno foi transferido)
                          const realSid=attSidFor(s.id,sid);
                          const present=realSid!==null;
                          const outraTurma=present&&realSid!==sid;
                          return (
                            <td key={sid}
                              title={bloq?"Inscrição trancada — não pode receber presença":(outraTurma?"Presença registrada na turma anterior (antes da transferência)":(present?"Presente — clique para remover":"Falta — clique para registrar presença"))}
                              style={{...d1.td,padding:"11px 4px",textAlign:"center",borderLeft:`1px solid ${D1.line2}`,fontFamily:FONT.mono,fontWeight:600,color:bloq&&!present?D1.ink3:(present?D1.ok:D1.alert),cursor:bloq&&!present?"not-allowed":"pointer",userSelect:"none"}}
                              onClick={()=>{ if(present){ setConfirmDel({studentId:s.id,studentName:s.name,sessionId:realSid as string}); } else if(!bloq){ onAddAttendance({id:s.id,name:s.name,dre:s.dre,turma:s.turma},sid); } }}>
                              {present?(outraTurma?"P*":"P"):(bloq?"–":"F")}
                            </td>
                          );
                        })}
                        <td style={{...d1.td,position:"sticky",right:118,background:bgFixa,zIndex:1,textAlign:"right",fontFamily:FONT.mono,fontWeight:600,color:bloq?D1.ink3:freqColor(pct),whiteSpace:"nowrap"}} title={`${pres} de ${sessionsForTurma.length} aulas`}>{pct}%</td>
                        <td style={{...d1.td,position:"sticky",right:0,background:bgFixa,zIndex:1}}>{bloq?<NeutralBadge>Trancada</NeutralBadge>:<SitBadge pct={pct}/>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{...d1.cap,position:"sticky",left:0,background:D1.paper,padding:"10px 12px",zIndex:1,borderTop:`1.5px solid ${D1.ink}`}}>Total</td>
                    <td style={{position:"sticky",left:100,background:D1.paper,padding:"10px 12px",fontSize:12,color:D1.ink2,zIndex:1,borderTop:`1.5px solid ${D1.ink}`}}>Presentes por aula</td>
                    {sessionsDisplay.map(sid=>{
                      const rate=getSessionRate(sid);
                      return <td key={sid} style={{padding:"10px 4px",fontFamily:FONT.mono,fontSize:11,fontWeight:600,color:freqColor(rate),textAlign:"center",background:D1.paper,borderTop:`1.5px solid ${D1.ink}`,borderLeft:`1px solid ${D1.line2}`}}>{rate}%</td>;
                    })}
                    <td style={{padding:"10px 12px",textAlign:"right",fontFamily:FONT.mono,fontWeight:600,fontSize:13,color:freqColor(avgFreq),position:"sticky",right:118,background:D1.paper,zIndex:1,borderTop:`1.5px solid ${D1.ink}`}}>{avgFreq}%</td>
                    <td style={{position:"sticky",right:0,background:D1.paper,zIndex:1,borderTop:`1.5px solid ${D1.ink}`}}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

      {confirmDel&&(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}><div style={{background:"white",borderRadius:20,padding:24,width:"100%",maxWidth:340}}><div style={{fontWeight:700,fontSize:16,color:"#0a1128",marginBottom:12}}>Remover Presença</div><div style={{fontSize:14,color:"#374151",marginBottom:20}}>Remover presença de <strong>{confirmDel.studentName}</strong> nesta data?</div><div style={{display:"flex",gap:8}}><button onClick={()=>setConfirmDel(null)} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1.5px solid #d1d5db",background:"white",cursor:"pointer",fontWeight:600}}>Cancelar</button><button onClick={async()=>{const d=confirmDel;setConfirmDel(null);await onDeleteAttendance(d.studentId,d.sessionId);}} style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:"#dc2626",color:"white",cursor:"pointer",fontWeight:600}}>Remover</button></div></div></div>)}
      {confirmDelSession&&(<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1001,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}><div style={{background:"white",borderRadius:20,padding:24,width:"100%",maxWidth:340}}><div style={{fontWeight:700,fontSize:16,color:"#0a1128",marginBottom:12}}>Apagar Sessão</div><div style={{fontSize:14,color:"#374151",marginBottom:20}}>Apagar <strong>todos os registros</strong> da sessão <strong>{sessLabel(confirmDelSession.sessionId)}</strong>? Esta ação não pode ser desfeita.</div><div style={{display:"flex",gap:8}}><button onClick={()=>setConfirmDelSession(null)} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1.5px solid #d1d5db",background:"white",cursor:"pointer",fontWeight:600}}>Cancelar</button><button onClick={async()=>{const d=confirmDelSession;setConfirmDelSession(null);try{const attRef=collection(db,periodBase(viewPeriodId)+"/attendance");const snap=await getDocs(query(attRef,where("sessionId","==",d.sessionId)));const batch=writeBatch(db);snap.docs.forEach(d2=>batch.delete(d2.ref));const sessDoc=sessions.find(s=>s.sessionId===d.sessionId||s.id===d.sessionId);if(sessDoc){batch.delete(doc(db,periodBase(viewPeriodId)+'/sessions/'+sessDoc.id));}await batch.commit();}catch(e:unknown){alert("Erro ao apagar sessão: "+(e instanceof Error?e.message:""));}}} style={{flex:1,padding:"10px 0",borderRadius:10,border:"none",background:"#dc2626",color:"white",cursor:"pointer",fontWeight:600}}>Apagar tudo</button></div></div></div>)}

      {/* Modal de edição em massa */}
      {bulkEditSession&&(()=>{
        const sess=sessions.find(s=>s.sessionId===bulkEditSession.sessionId||s.id===bulkEditSession.sessionId);
        const dt=sess?.createdAt?(sess.createdAt.toDate?sess.createdAt.toDate():new Date(sess.createdAt)):null;
        const dtLabel=dt?dt.toLocaleDateString("pt-BR",{weekday:"long",day:"2-digit",month:"2-digit",year:"numeric"}):"";
        const ausentes=turmaStudents.length-bulkPresentCount;
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div style={{background:"#fff",borderRadius:20,width:"100%",maxWidth:560,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,.25)"}}>

              {/* Header */}
              <div style={{padding:"20px 24px 14px",borderBottom:`1px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div>
                    <div style={{fontSize:11,color:T.ink50,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",marginBottom:2}}>Editar em massa · Turma {rTurma}</div>
                    <div style={{fontFamily:T.display,fontSize:19,fontWeight:700,color:T.navy,lineHeight:1.2}}>{bulkEditSession.label}</div>
                    {dtLabel&&<div style={{fontSize:12,color:T.ink70,marginTop:2}}>{dtLabel}</div>}
                  </div>
                  <button onClick={bulkCancel} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:T.ink50,padding:0,lineHeight:1}}>✕</button>
                </div>
                <div style={{display:"flex",gap:10,marginTop:12,fontSize:12}}>
                  <div style={{flex:1,background:T.okSoft,color:T.ok,padding:"6px 10px",borderRadius:8,textAlign:"center",fontWeight:700}}>✓ {bulkPresentCount} presentes</div>
                  <div style={{flex:1,background:T.alertSoft,color:T.alert,padding:"6px 10px",borderRadius:8,textAlign:"center",fontWeight:700}}>✗ {ausentes} ausentes</div>
                  <div style={{flex:1,background:bulkChangeCount>0?"#fef3c7":T.bg,color:bulkChangeCount>0?"#92400e":T.ink50,padding:"6px 10px",borderRadius:8,textAlign:"center",fontWeight:700}}>● {bulkChangeCount} alteração{bulkChangeCount===1?"":"ões"}</div>
                </div>
              </div>

              {/* Filtros */}
              <div style={{padding:"12px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                <div style={{display:"flex",background:T.bg,padding:3,borderRadius:8,gap:2}}>
                  {([["all","Todos"],["absent","Só ausentes"],["present","Só presentes"]] as const).map(([k,lbl])=>(
                    <div key={k} style={{padding:"5px 12px",borderRadius:6,fontSize:12,fontWeight:700,background:bulkFilter===k?T.navy:"transparent",color:bulkFilter===k?"#fff":T.ink70,cursor:"pointer"}} onClick={()=>setBulkFilter(k)}>{lbl}</div>
                  ))}
                </div>
                <input style={{flex:1,minWidth:140,padding:"6px 10px",border:`1px solid ${T.border}`,borderRadius:8,fontSize:12,outline:"none"}} placeholder="Buscar nome ou DRE…" value={bulkSearch} onChange={e=>setBulkSearch(e.target.value)} />
              </div>

              {/* Atalhos */}
              <div style={{padding:"10px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>bulkMarkAllVisible(true)} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${T.ok}33`,background:T.okSoft,color:T.ok,fontSize:11,fontWeight:700,cursor:"pointer"}}>✓ Marcar todos visíveis presentes</button>
                <button onClick={()=>bulkMarkAllVisible(false)} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${T.alert}33`,background:T.alertSoft,color:T.alert,fontSize:11,fontWeight:700,cursor:"pointer"}}>✗ Marcar todos visíveis ausentes</button>
                <button onClick={bulkInvertVisible} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:"#fff",color:T.navy,fontSize:11,fontWeight:700,cursor:"pointer"}}>⇄ Inverter visíveis</button>
                <div style={{marginLeft:"auto",fontSize:11,color:T.ink50,alignSelf:"center"}}>{bulkVisibleStudents.length} de {turmaStudents.length}</div>
              </div>

              {/* Lista de alunos */}
              <div style={{flex:1,overflowY:"auto",padding:"8px 16px"}}>
                {bulkVisibleStudents.length===0?(
                  <div style={{textAlign:"center",padding:"30px 0",color:T.ink50,fontSize:13}}>Nenhum aluno corresponde ao filtro.</div>
                ):bulkVisibleStudents.map(s=>{
                  const initial=hasAtt(s.id,bulkEditSession.sessionId);
                  const current=bulkState[s.id]??initial;
                  const changed=current!==initial;
                  // Inscricao trancada: aparece em cinza e nao pode ser marcada
                  const bloq=!!s.blocked;
                  return (
                    <label key={s.id} title={bloq?"Inscrição trancada — não pode receber presença":undefined} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:bloq?"not-allowed":"pointer",background:bloq?"#f1f5f9":(changed?"#fffbeb":"transparent"),border:`1px solid ${bloq?T.border:(changed?"#fbbf24":"transparent")}`,marginBottom:2,opacity:bloq?0.6:1}}>
                      <input type="checkbox" disabled={bloq} checked={bloq?false:current} onChange={()=>{ if(bloq) return; setBulkState(prev=>({...prev,[s.id]:!(prev[s.id]??initial)})); }} style={{width:18,height:18,cursor:bloq?"not-allowed":"pointer",accentColor:T.ok}} />
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,color:bloq?T.ink50:T.navy,lineHeight:1.2}}>{s.name}</div>
                        <div style={{fontSize:10,color:T.ink50,fontFamily:T.mono,marginTop:1}}>DRE {s.dre} · Turma {s.turma}{bloq?" · 🔒 trancada":""}</div>
                      </div>
                      {bloq
                        ? <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,background:T.ink10,color:T.ink50,fontFamily:T.mono}}>TRANCADA</span>
                        : <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:4,background:current?T.okSoft:T.alertSoft,color:current?T.ok:T.alert,fontFamily:T.mono}}>{current?"PRES":"AUS"}</span>}
                      {!bloq&&changed&&<span style={{fontSize:9,fontWeight:700,color:"#92400e",fontFamily:T.mono}}>●</span>}
                    </label>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{padding:"14px 24px",borderTop:`1px solid ${T.border}`,display:"flex",gap:10,background:T.bg}}>
                <button onClick={bulkCancel} disabled={bulkSaving} style={{flex:1,padding:"10px 0",borderRadius:10,border:`1.5px solid ${T.border}`,background:"#fff",cursor:bulkSaving?"not-allowed":"pointer",fontWeight:600,color:T.ink70}}>Cancelar</button>
                <button onClick={bulkSave} disabled={bulkSaving||bulkChangeCount===0} style={{flex:2,padding:"10px 0",borderRadius:10,border:"none",background:bulkChangeCount===0?T.ink30:T.heroGrad,color:"#fff",cursor:(bulkSaving||bulkChangeCount===0)?"not-allowed":"pointer",fontWeight:700,fontSize:13,letterSpacing:0.3}}>{bulkSaving?"Salvando…":`Salvar (${bulkChangeCount} alteraç${bulkChangeCount===1?"ão":"ões"})`}</button>
              </div>

            </div>
          </div>
        );
      })()}

      {studentDetail&&(()=>{
        const stuAtt=attendance.filter(a=>a.studentId===studentDetail.id).sort((a,b)=>a.sessionId.localeCompare(b.sessionId));
        const allSess=sessionsForTurma;
        const totalP=allSess.filter(sid=>presenteEm(studentDetail.id,sid)).length;
        const totalS=allSess.length;
        const pct=totalS>0?Math.round((totalP/totalS)*100):0;
        const statusColor=pct>=75?"#16a34a":"#dc2626";
        return (<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setStudentDetail(null)}>
          <div style={{background:"white",borderRadius:20,padding:24,width:"100%",maxWidth:420,maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontWeight:700,fontSize:17,color:"#0a1128"}}>{studentDetail.name}</div>
                <div style={{fontSize:12,color:"#64748b",marginTop:2}}>DRE: {studentDetail.dre}</div>
              </div>
              <button onClick={()=>setStudentDetail(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:20,color:"#64748b",padding:0,lineHeight:1}}>✕</button>
            </div>
            <div style={{display:"flex",gap:12,marginBottom:20}}>
              <div style={{flex:1,background:"#f0fdf4",borderRadius:12,padding:"10px 14px",textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:700,color:"#16a34a"}}>{totalP}</div>
                <div style={{fontSize:11,color:"#64748b"}}>Presenças</div>
              </div>
              <div style={{flex:1,background:"#fef2f2",borderRadius:12,padding:"10px 14px",textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:700,color:"#dc2626"}}>{totalS-totalP}</div>
                <div style={{fontSize:11,color:"#64748b"}}>Faltas</div>
              </div>
              <div style={{flex:1,background:"#f8fafc",borderRadius:12,padding:"10px 14px",textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:700,color:statusColor}}>{pct}%</div>
                <div style={{fontSize:11,color:"#64748b"}}>Frequência</div>
              </div>
              <div style={{flex:1,background:"#eff6ff",borderRadius:12,padding:"10px 14px",textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:700,color:"#0369a1"}}>{totalS}</div>
                <div style={{fontSize:11,color:"#64748b"}}>Aulas dadas</div>
              </div>
            </div>
            <div style={{fontWeight:700,fontSize:13,color:"#0a1128",marginBottom:10}}>Histórico de aulas</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {allSess.map(sid=>{
                const realSid=attSidFor(studentDetail.id,sid);
                const present=realSid!==null;
                const outraTurma=present&&realSid!==sid;
                return (<div key={sid} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",borderRadius:10,background:present?"#f0fdf4":"#fef2f2",border:`1px solid ${present?"#bbf7d0":"#fecaca"}`}}>
                  <span style={{fontSize:13,fontWeight:600,color:"#374151"}}>{sessLabel(sid)}{outraTurma&&<span style={{fontSize:11,color:"#0f766e",fontWeight:600}}> · turma anterior</span>}</span>
                  {present
                    ?(<button onClick={()=>setConfirmDel({studentId:studentDetail.id,studentName:studentDetail.name,sessionId:realSid as string})} title="Presença — clique para remover" aria-label="Presença" style={{background:"none",border:"none",cursor:"pointer",fontSize:18,padding:0,lineHeight:1}}>&#x1F7E2;</button>)
                    :(<button onClick={()=>onAddAttendance({id:studentDetail.id,name:studentDetail.name,dre:studentDetail.dre,turma:stuAtt[0]?.turma||students.find(s=>s.id===studentDetail.id)?.turma||""},sid)} title="Adicionar presença" style={{background:"none",border:"1.5px dashed #94a3b8",borderRadius:8,cursor:"pointer",fontSize:13,padding:"2px 8px",color:"#64748b"}}>+ presença</button>)
                  }
                </div>);
              })}
            </div>
            {allSess.length===0&&<div style={{textAlign:"center",color:"#94a3b8",fontSize:13,padding:"20px 0"}}>Nenhuma aula registrada.</div>}
          </div>
        </div>);
      })()}
      </div>
    </div>
  );
}
